/**
 * RefundWorkerStatusCard — StreamX Sprint 3a.
 *
 * Premium ops-visibility card showing the refund worker's health:
 * queue depth, status distribution, last drain, dead-letter alerts.
 *
 * Doctrine
 * ────────
 *   • Calm reliability signal, not error logs
 *   • Auto-refreshes every 30 seconds (matches worker interval)
 *   • Allows manual drain trigger (super_admin only)
 *   • Highlights dead-lettered tasks as red so they never silently rot
 */
import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  RefreshCcw, Banknote, AlertTriangle, Clock, CheckCircle2, Loader2, Skull,
} from 'lucide-react';

const STATUS_META = {
  queued:      { label: 'Queued',      tone: '#FFA500', icon: Clock },
  in_flight:   { label: 'In-flight',   tone: '#60a5fa', icon: Loader2 },
  succeeded:   { label: 'Succeeded',   tone: '#10b981', icon: CheckCircle2 },
  failed:      { label: 'Failed',      tone: '#f59e0b', icon: AlertTriangle },
  dead_letter: { label: 'Dead-letter', tone: 'hsl(0 70% 55%)', icon: Skull },
};

const ORDER = ['queued', 'in_flight', 'succeeded', 'failed', 'dead_letter'];

export default function RefundWorkerStatusCard() {
  const [counts, setCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draining, setDraining] = useState(false);
  const [lastFetchAt, setLastFetchAt] = useState(null);
  const [err, setErr] = useState('');

  const fetchStatus = useCallback(async () => {
    try {
      const r = await api.get('/arenax/admin/refund-worker/queue?limit=20');
      setCounts(r.data?.counts || {});
      setTotal(r.data?.total || 0);
      setRecent(r.data?.tasks || []);
      setLastFetchAt(new Date());
      setErr('');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not load status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const id = setInterval(fetchStatus, 30_000);
    return () => clearInterval(id);
  }, [fetchStatus]);

  const handleDrain = async () => {
    setDraining(true);
    try {
      const r = await api.post('/arenax/admin/refund-worker/drain');
      const c = r.data?.counters || {};
      toast.success(
        c.processed > 0
          ? `Drained ${c.processed} · ${c.succeeded || 0} succeeded · ${c.dead_letter || 0} dead-lettered`
          : 'Nothing to drain right now.',
      );
      await fetchStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Drain failed');
    } finally { setDraining(false); }
  };

  const deadLetter = counts.dead_letter || 0;
  const queued = counts.queued || 0;
  const succeeded = counts.succeeded || 0;

  return (
    <div
      className="p-5 rounded-xl border-2"
      style={{
        background: 'linear-gradient(135deg, #07111F 0%, #0a1628 100%)',
        borderColor: deadLetter > 0 ? 'hsl(0 70% 55%)' : '#22314F',
      }}
      data-testid="refund-worker-status-card"
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <Banknote className="h-4 w-4" style={{ color: '#FF5A1F' }} />
            <h3 className="text-base font-black text-white">Refund worker health</h3>
          </div>
          <div className="text-[11px] mt-1" style={{ color: '#8FA3C9' }}>
            Refresh every 30s · Mock simulation mode unless `REFUND_WORKER_LIVE=true`
          </div>
        </div>
        <button
          onClick={handleDrain}
          disabled={draining}
          className="text-[11px] px-3 py-1.5 rounded-lg border font-bold uppercase tracking-wider transition-colors hover:bg-white/5 disabled:opacity-50"
          style={{ borderColor: '#22314F', color: '#FF5A1F' }}
          data-testid="refund-worker-manual-drain"
        >
          {draining ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Run drain'}
        </button>
      </div>

      {/* Headline */}
      <div className="flex items-baseline gap-2 mb-4">
        <span className="text-4xl font-black tabular-nums text-white">{total}</span>
        <span className="text-xs uppercase tracking-[0.18em]" style={{ color: '#8FA3C9' }}>
          total tasks ever queued
        </span>
      </div>

      {/* Status distribution */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-5">
        {ORDER.map((status) => {
          const meta = STATUS_META[status];
          const Icon = meta.icon;
          const value = counts[status] || 0;
          return (
            <div
              key={status}
              className="p-3 rounded-lg border"
              style={{
                background: 'rgba(255, 255, 255, 0.02)',
                borderColor: status === 'dead_letter' && value > 0 ? 'hsl(0 70% 55%)' : '#22314F',
              }}
              data-testid={`refund-worker-count-${status}`}
            >
              <div className="flex items-center gap-1.5 mb-1.5">
                <Icon className="h-3 w-3" style={{ color: meta.tone }} />
                <span className="text-[9px] uppercase tracking-[0.16em] font-bold"
                      style={{ color: meta.tone }}>
                  {meta.label}
                </span>
              </div>
              <div className="text-2xl font-black tabular-nums text-white">{value}</div>
            </div>
          );
        })}
      </div>

      {/* Loading / error state */}
      {loading && (
        <div className="text-[11px] text-center py-3" style={{ color: '#8FA3C9' }}>
          Loading status…
        </div>
      )}
      {err && (
        <div className="text-[11px] py-2 px-3 rounded-md"
             style={{ background: 'rgba(244, 63, 94, 0.1)', color: '#fb7185' }}
             data-testid="refund-worker-error">
          {err}
        </div>
      )}

      {/* Dead-letter callout */}
      {deadLetter > 0 && (
        <div
          className="mt-4 p-3 rounded-lg flex items-start gap-2"
          style={{
            background: 'rgba(244, 63, 94, 0.08)',
            border: '1px solid hsl(0 70% 55% / 0.4)',
          }}
          data-testid="refund-worker-dead-letter-alert"
        >
          <Skull className="h-4 w-4 shrink-0 mt-0.5" style={{ color: 'hsl(0 70% 55%)' }} />
          <div className="text-xs leading-relaxed">
            <div className="font-bold text-white">{deadLetter} refund{deadLetter === 1 ? '' : 's'} dead-lettered</div>
            <div style={{ color: '#fbcfe8' }}>
              Manual investigation required. Each task exceeded 5 retry attempts.
              Inspect via /api/arenax/admin/refund-worker/queue?status=dead_letter.
            </div>
          </div>
        </div>
      )}

      {/* Recent tasks footer */}
      {!loading && recent.length === 0 && !err && total === 0 && (
        <div className="text-[11px] italic text-center py-3" style={{ color: '#5a708a' }}
             data-testid="refund-worker-empty">
          Refund queue is empty. The worker is idle and waiting.
        </div>
      )}

      {!loading && recent.length > 0 && (
        <div className="mt-4 pt-3 border-t" style={{ borderColor: '#22314F' }}>
          <div className="text-[9px] uppercase tracking-[0.16em] font-bold mb-2" style={{ color: '#8FA3C9' }}>
            Recent activity
          </div>
          <div className="space-y-1.5">
            {recent.slice(0, 5).map((t) => {
              const meta = STATUS_META[t.status] || STATUS_META.queued;
              return (
                <div key={t.id} className="flex items-center justify-between text-[11px] gap-2">
                  <span className="font-mono truncate flex-1" style={{ color: '#cbd5e1' }}>
                    ₹{((t.amount_paise || 0) / 100).toLocaleString('en-IN')}
                    <span className="text-[9px] ml-2" style={{ color: '#5a708a' }}>
                      {t.id?.slice(0, 12)}…
                    </span>
                  </span>
                  <span className="font-bold uppercase tracking-wider text-[9px]"
                        style={{ color: meta.tone }}>
                    {meta.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="text-[9px] mt-3 text-right font-mono" style={{ color: '#5a708a' }}>
        {lastFetchAt ? `Last checked ${lastFetchAt.toLocaleTimeString()}` : ''}
      </div>

      <button
        onClick={fetchStatus}
        className="absolute opacity-0 pointer-events-none"
        aria-hidden
      >
        <RefreshCcw className="h-3 w-3" />
      </button>
    </div>
  );
}
